import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring, useMotionTemplate } from "framer-motion";
import { Sparkles, ArrowDown, BadgeCheck, PenTool, PackageCheck } from "lucide-react";
import { IMAGES } from "@/constants/site";
import { scrollToSection } from "@/lib/scroll";

const EASE = [0.16, 1, 0.3, 1];

function RevealLine({ children, delay, className = "" }) {
  return (
    <span className={`block overflow-hidden ${className}`}>
      <motion.span
        className="block"
        initial={{ y: "115%" }}
        animate={{ y: 0 }}
        transition={{ duration: 0.9, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end start"] });
  const yCard = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const yGlow = useTransform(scrollYProgress, [0, 1], [0, -60]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rX = useSpring(useTransform(my, [-0.5, 0.5], [12, -12]), { stiffness: 140, damping: 18 });
  const rY = useSpring(useTransform(mx, [-0.5, 0.5], [-14, 14]), { stiffness: 140, damping: 18 });
  const shineX = useTransform(mx, [-0.5, 0.5], [10, 90]);
  const shineY = useTransform(my, [-0.5, 0.5], [10, 90]);
  const shine = useMotionTemplate`radial-gradient(circle at ${shineX}% ${shineY}%, rgba(255,255,255,0.28), rgba(255,255,255,0.05) 35%, transparent 60%)`;

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <section id="accueil" ref={sectionRef} className="relative overflow-hidden pt-28 pb-16 md:pt-36 md:pb-24">
      {/* ambient glows */}
      <motion.div style={{ y: yGlow }} aria-hidden="true" className="pointer-events-none absolute inset-0">
        <div className="absolute -top-32 left-1/4 h-[480px] w-[480px] rounded-full bg-fuchsia-600/20 blur-[140px]" />
        <div className="absolute top-1/3 -right-24 h-[420px] w-[420px] rounded-full bg-cyan-500/20 blur-[140px]" />
        <div className="absolute bottom-0 left-0 h-[300px] w-[300px] rounded-full bg-violet-600/15 blur-[120px]" />
      </motion.div>
      <div className="bg-grid absolute inset-0 opacity-[0.35]" aria-hidden="true" />

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-5 md:px-8 lg:grid-cols-12 lg:gap-8">
        {/* Copy */}
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: EASE }}
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-cyan-300 sm:text-[11px]"
          >
            <Sparkles size={13} className="text-amber-300" />
            Créations 100% personnalisées — made in France
          </motion.p>

          <h1 className="font-display text-[2.1rem] font-extrabold leading-[1.02] tracking-tight text-white sm:text-6xl lg:text-[4.6rem]">
            <RevealLine delay={0.15}>DES CARTES TCG</RevealLine>
            <RevealLine delay={0.28}>
              <span className="text-holo">100% SUR MESURE.</span>
            </RevealLine>
            <RevealLine delay={0.41} className="text-[1.35rem] text-[#a09bb5] sm:text-3xl lg:text-4xl">
              Des pièces uniques de collection.
            </RevealLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.6, ease: EASE }}
            className="mt-7 max-w-xl text-base leading-relaxed text-[#a09bb5] sm:text-lg"
          >
            Pokebastos crée des tableaux uniques de 20 × 20 cm : votre personnage illustré sur une
            carte custom imprimée en qualité premium, montée au centre sous boîtier acrylique
            transparent, sur un décor qui reprend exactement son illustration.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.75, ease: EASE }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              type="button"
              data-testid="hero-cta-gallery"
              onClick={() => scrollToSection("galerie")}
              className="btn-holo group inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-bold text-[#08070d] sm:text-base"
            >
              Découvrir la galerie
              <ArrowDown size={16} className="transition-transform duration-300 group-hover:translate-y-0.5" />
            </button>
            <button
              type="button"
              data-testid="hero-cta-devis"
              onClick={() => scrollToSection("contact")}
              className="inline-flex items-center gap-2 rounded-full border border-white/15 px-7 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:border-cyan-400/50 hover:bg-white/5 sm:text-base"
            >
              Demander un devis
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.95 }}
            className="mt-12 flex flex-wrap gap-x-10 gap-y-5"
          >
            {[
              { icon: PenTool, label: "100% personnalisé" },
              { icon: BadgeCheck, label: "Pièces uniques signées" },
              { icon: PackageCheck, label: "Envoi sécurisé et suivi" },
            ].map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-2.5">
                <span className="flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 bg-white/5">
                  <Icon size={16} className="text-amber-300" />
                </span>
                <span className="text-sm font-medium text-[#c8c4d8]">{label}</span>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Card visual */}
        <motion.div style={{ y: yCard }} className="relative lg:col-span-5" initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1, delay: 0.4, ease: EASE }}>
          <div
            data-testid="hero-tilt-card"
            onMouseMove={onMove}
            onMouseLeave={onLeave}
            className="relative mx-auto w-[240px] cursor-pointer sm:w-[300px] lg:w-[340px]"
            style={{ perspective: 1000 }}
          >
            <div className="absolute -inset-10 rounded-full bg-gradient-to-tr from-cyan-500/30 via-fuchsia-500/30 to-amber-400/30 blur-[70px]" aria-hidden="true" />
            <motion.div
              style={{ rotateX: rX, rotateY: rY, transformStyle: "preserve-3d" }}
              className="relative overflow-hidden rounded-[18px] border border-white/15 shadow-[0_35px_80px_-20px_rgba(0,0,0,0.8)]"
            >
              <img
                src={IMAGES.heroCard}
                alt="Exemple de carte TCG personnalisée créée par Pokebastos"
                className="h-auto w-full"
              />
              <motion.div style={{ background: shine }} className="pointer-events-none absolute inset-0" aria-hidden="true" />
              <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(115deg,transparent_35%,rgba(255,255,255,0.18)_48%,transparent_60%)]" aria-hidden="true" />
            </motion.div>

            <motion.div
              animate={{ y: [0, -12, 0] }}
              transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -left-8 top-8 hidden rounded-xl border border-white/10 bg-[#100e19]/90 px-4 py-2.5 backdrop-blur-md sm:block"
              style={{ transform: "translateZ(50px)" }}
            >
              <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-cyan-300">Création</p>
              <p className="text-sm font-semibold text-white">Illustration originale</p>
            </motion.div>
            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 5.2, repeat: Infinity, ease: "easeInOut", delay: 0.6 }}
              className="absolute -right-6 bottom-14 hidden rounded-xl border border-white/10 bg-[#100e19]/90 px-4 py-2.5 backdrop-blur-md sm:block"
              style={{ transform: "translateZ(60px)" }}
            >
              <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-fuchsia-400">Exemplaire</p>
              <p className="text-sm font-semibold text-white">Pièce unique</p>
            </motion.div>
          </div>

          <p className="mt-6 text-center font-mono text-[10px] uppercase tracking-[0.25em] text-[#6c6685]">
            Création originale — pièce unique personnalisée
          </p>
        </motion.div>
      </div>
    </section>
  );
}
